import type { CreditSourceSystem, CreditUsageCategory } from "@/lib/billing/creditsLedger";

/**
 * How each ATW system will push usage INTO the org Credits ledger that
 * Directory Billing and Platform both read. Emission mostly lives outside
 * atw-directory (Gray-Kyle/ATWNetworkWebpage, Workers, Mail, Workforce,
 * Agents). This file is the integration checklist for follow-up PRs.
 *
 * Every emit must produce an auditable CreditLedgerEntry (summary, category,
 * quantity, credits, sourceSystem, occurredAt, actor if any, relatedResources).
 * Category ids must match Platform CREDIT_LEDGER_CATEGORIES.
 */

export type CreditEmitHook = {
  sourceSystem: CreditSourceSystem;
  /** Product surface that owns the trigger. */
  product: string;
  /** Likely repo or Worker where the emit should land. */
  emitOwner: string;
  categories: CreditUsageCategory[];
  /** What action causes an event. */
  trigger: string;
  /** Status of collection wiring. */
  status: "planned" | "partial" | "directory_trigger_only";
  notes: string;
};

export const CREDIT_EMIT_PLAN: CreditEmitHook[] = [
  {
    sourceSystem: "directory",
    product: "Directory mailbox toggle",
    emitOwner: "atw-directory (trigger) + platform billing Worker (ledger write)",
    categories: ["hosted_inbox"],
    trigger:
      "Person Mail tab turns inbox on or off. Directory does not price the seat; it signals mailbox entitlement. Platform bills hosted_inbox for active inbox seats each period.",
    status: "directory_trigger_only",
    notes:
      "Keep Person detail as trigger only. Do not show dollar amounts on Person pages. Follow-up: platform job that snapshots active mailbox seats per orgId each billing period.",
  },
  {
    sourceSystem: "one",
    product: "ATW One (CRM, Finance, records, API)",
    emitOwner: "Gray-Kyle/ATWNetworkWebpage peel Workers (atw-one-api / atw-one-crm / atw-one-finance) or Amplify until peeled",
    categories: ["write", "read", "records", "automations", "storage"],
    trigger:
      "CRM/Finance API reads and writes, record create/update events, automation runs, and app-level storage growth attributed to One.",
    status: "planned",
    notes:
      "Prefer emit at the native Worker edge after auth, not from Pages UI. Hub and Pay are base-tier (not customer-paid); do not emit customer Credits for Pay or Hub.",
  },
  {
    sourceSystem: "mail",
    product: "Mail / WorkSuite inbound and outbound email",
    emitOwner: "mail.atw.net / WorkSuite mail plane (Gray-Kyle/ATWNetworkWebpage or mail Worker)",
    categories: ["email_in", "email_out", "storage", "sms"],
    trigger:
      "Message accepted or sent, SMS from mail plane, mailbox storage meters.",
    status: "planned",
    notes:
      "Directory mailbox on/off is the seat signal; Mail emits per-message and storage usage. Align orgId with Directory membership org.",
  },
  {
    sourceSystem: "workforce",
    product: "Workforce runs",
    emitOwner: "Workforce app / Workers (thin existing Billing stubs toward Directory)",
    categories: ["workforce_runs"],
    trigger: "Each Workforce run completion attributed to the org.",
    status: "partial",
    notes:
      "Workforce may already show local billing stubs. Follow-up PR: stop duplicating org billing UI there; emit CreditUsageEvent and deep-link to Directory /billing.",
  },
  {
    sourceSystem: "ai",
    product: "ATW AI / Agents (tokens, seats already gated in Agents)",
    emitOwner: "agents.atw.net + Agents Worker + One AI support turns",
    categories: ["ai_tokens"],
    trigger: "Model completions and tool calls that consume tokens for the org.",
    status: "partial",
    notes:
      "Agents UI can keep seat configuration; Credits spend rolls into Directory Billing. One AI support turns already emit via Platform creditsLedger.",
  },
  {
    sourceSystem: "sandbox",
    product: "Per-system sandbox copy and active use",
    emitOwner: "Each system sandbox controller + shared sandbox metering helper",
    categories: ["sandbox_copy", "sandbox_use"],
    trigger:
      "Prod-to-sandbox copy (show estimated Credits before confirm) and active sandbox compute/storage beyond idle base.",
    status: "planned",
    notes:
      "Idle sandbox included in base tier emits nothing. Copy must preview cost using the org frozen rate card before charge.",
  },
  {
    sourceSystem: "voice",
    product: "Voice minutes",
    emitOwner: "Platform / voice provider integration Worker",
    categories: ["voice"],
    trigger: "Voice call completion (minutes).",
    status: "planned",
    notes: "Voice is Credits-metered; Pay and Hub remain non-customer-paid base tier.",
  },
  {
    sourceSystem: "sms",
    product: "Platform-routed SMS",
    emitOwner: "Platform SMS integration when not owned by Mail",
    categories: ["sms"],
    trigger: "Platform-routed SMS sends attributed to the org.",
    status: "planned",
    notes: "Prefer Mail sourceSystem when the mail plane owns the send.",
  },
];

/** Suggested follow-up PRs so collection is not forgotten. */
export const CREDIT_EMIT_FOLLOW_UPS: string[] = [
  "Platform: keep GET /api/billing/me/credits* and /api/platform/billing/orgs/:orgId/credits* on the Credits ledger (CREDIT# rows). Peel to a native Worker after the finance hybrid bake.",
  "Platform: store Super Admin billing grants (billingAccessGranted on /api/directory/me) so non-super-admins can open Billing only when granted.",
  "Directory: after mailbox toggle succeeds, call platform seat snapshot or enqueue hosted_inbox reconciliation (no pricing on Person UI).",
  "One / CRM / Finance Workers: emit write, read, records, automations with orgId, reason, and related resource ids.",
  "Mail / WorkSuite: emit email_in, email_out, storage, sms with message/domain resource ids.",
  "Workforce: thin local Billing stubs toward Directory /billing; emit workforce_runs with run ids.",
  "Agents: emit ai_tokens against org frozen rate card weights with seat/run ids.",
  "Sandbox: cost preview on copy using rate card; emit sandbox_copy and sandbox_use with sandbox ids.",
  "Voice: emit voice from the voice integration Worker with call/account resource ids.",
];
